import { useTranslation } from 'react-i18next';
import { resolveLocalizedName } from 'pi-kiosk-shared';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../../../app/pwa/customerFormDirtyGate.js';
import { toCustomerFacingErrorMessage } from '../../../../shared/errors/toCustomerFacingErrorMessage.js';
import { Card } from '../../../../shared/ui/Card.js';
import { Button } from '../../../../shared/ui/Button.js';
import { CollectStepSheet } from '../../../checkout/components/CollectStepSheet.js';
import { FulfillmentModalityPanel } from '../../../checkout/components/FulfillmentModalityPanel.js';
import { useShippingModalityDraft } from '../../../checkout/hooks/useShippingModalityDraft.js';
import { resolveRetailHandoffChoices } from '../../../checkout/components/collectHandoffChoices.js';
import {
  resolvePickupCollectIntroKey,
  shouldShowSoleCollectIntroBanner,
} from '../../../checkout/components/collectSoleOptionPresentation.js';
import { shouldShowNowTimingUnderLaterLock } from '../../../shop/presenters/shopCheckoutPresenter.js';
import { resolveShopLocationLabelWithT } from '../../../shops/resolveShopLocationLabel.js';
import type { UsePhoneFirstCheckoutScreenResult } from '../../hooks/usePhoneFirstCheckoutScreen.js';

export type PhoneFirstCheckoutCollectStepViewProps = Pick<
  UsePhoneFirstCheckoutScreenResult,
  | 't'
  | 'sessionError'
  | 'sessionLoading'
  | 'salesPointId'
  | 'pickupPoints'
  | 'pickupPointsQuery'
  | 'collectPointsLoadError'
  | 'effectiveCollectSelection'
  | 'scheduledPickupEnabled'
  | 'tenantOpsMode'
  | 'effectivePickupSlot'
  | 'setPickupSlot'
  | 'setCollectSelection'
  | 'lockedCollectTiming'
  | 'allowedPickupHandoffModes'
  | 'effectiveHandoffMode'
  | 'setSelectedHandoffMode'
  | 'selfCollectOnlyGate'
  | 'selfCollectConfirmAtKiosk'
  | 'selfCollectConfirmed'
  | 'setSelfCollectConfirmed'
  | 'kioskInfo'
  | 'collectContinueBlocked'
  | 'updateCollect'
  | 'handleCollectContinue'
  | 'handleShippingContinue'
  | 'shippingContinuePending'
  | 'paymentError'
>;

export function PhoneFirstCheckoutCollectStepView(
  props: PhoneFirstCheckoutCollectStepViewProps,
): JSX.Element {
  const {
    t,
    sessionError,
    sessionLoading,
    salesPointId,
    pickupPoints,
    pickupPointsQuery,
    collectPointsLoadError,
    effectiveCollectSelection,
    scheduledPickupEnabled,
    tenantOpsMode,
    effectivePickupSlot,
    setPickupSlot,
    setCollectSelection,
    lockedCollectTiming,
    allowedPickupHandoffModes,
    effectiveHandoffMode,
    setSelectedHandoffMode,
    selfCollectOnlyGate,
    selfCollectConfirmAtKiosk,
    selfCollectConfirmed,
    setSelfCollectConfirmed,
    kioskInfo,
    collectContinueBlocked,
    updateCollect,
    handleCollectContinue,
    handleShippingContinue,
    shippingContinuePending,
    paymentError,
  } = props;

  const { i18n } = useTranslation('customer');

  const isDraftDirty =
    effectiveCollectSelection !== null ||
    effectivePickupSlot !== null ||
    selfCollectConfirmed;

  const preserveUsableNow = shouldShowNowTimingUnderLaterLock(
    lockedCollectTiming,
    effectiveCollectSelection,
  );
  const showNowTiming =
    lockedCollectTiming === undefined ||
    lockedCollectTiming === 'NOW' ||
    preserveUsableNow;
  const showLaterTiming =
    !preserveUsableNow &&
    (lockedCollectTiming === undefined || lockedCollectTiming === 'LATER') &&
    scheduledPickupEnabled;
  const soleInput = {
    handoffChoiceCount: resolveRetailHandoffChoices(allowedPickupHandoffModes).length,
    showNowTiming,
    showLaterTiming,
    pickupPointCount: pickupPoints.length,
  };
  const showSoleIntroBanner = shouldShowSoleCollectIntroBanner(soleInput);
  const shippingDraft = useShippingModalityDraft({ salesPointId });
  const shippingSelected =
    shippingDraft.modalityVisible && shippingDraft.modality === 'SHIPPING';
  /**
   * Mirror ShopCheckoutOverlays CollectStepOverlay gateBlocked:
   * SHIPPING → !shippingReady || pending (policy + address via draft; G15 uiLanguage inside draft).
   * PICKUP → collectContinueBlocked || pending.
   */
  const continueBlocked = shippingSelected
    ? !shippingDraft.shippingReady || shippingContinuePending || sessionLoading
    : collectContinueBlocked || updateCollect.isPending || sessionLoading;
  const continuePending = shippingSelected
    ? shippingContinuePending
    : updateCollect.isPending;

  return (
    <Card padded>
      <div
        className="flex flex-col gap-4"
        {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
      >
        {sessionError !== null ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {toCustomerFacingErrorMessage(sessionError, {
              fallback: t('checkout.sessionLoadError'),
            })}
          </p>
        ) : null}
        {sessionLoading ? (
          <p className="text-sm text-[var(--color-on-surface-muted)]">{t('checkout.loadingOrder')}</p>
        ) : null}
        {showSoleIntroBanner && !shippingSelected ? (
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            role="status"
            data-testid="customer-pickup-sole-intro"
          >
            {t(resolvePickupCollectIntroKey(soleInput))}
          </p>
        ) : null}
        <FulfillmentModalityPanel
          eligible={shippingDraft.eligible}
          eligibleLoading={shippingDraft.eligibleLoading}
          modality={shippingDraft.modality}
          onModalityChange={shippingDraft.setModality}
          address={shippingDraft.address}
          onAddressChange={shippingDraft.setAddress}
          saveAddressToMembership={shippingDraft.saveAddressToMembership}
          onSaveAddressToMembershipChange={shippingDraft.setSaveAddressToMembership}
        />
        {salesPointId !== null && salesPointId > 0 && !shippingSelected ? (
          <CollectStepSheet
            kioskId={salesPointId}
            points={pickupPoints}
            pointsLoading={pickupPointsQuery.isLoading}
            pointsLoadError={collectPointsLoadError}
            onPointsRetry={
              pickupPointsQuery.isError
                ? () => {
                    void pickupPointsQuery.refetch();
                  }
                : undefined
            }
            collect={effectiveCollectSelection}
            scheduledPickupEnabled={scheduledPickupEnabled}
            opsMode={tenantOpsMode}
            pickupSlot={effectivePickupSlot}
            onPickupSlotChange={setPickupSlot}
            onCollectChange={setCollectSelection}
            lockedCollectTiming={lockedCollectTiming}
            allowedPickupHandoffModes={allowedPickupHandoffModes}
            selectedPickupHandoffMode={effectiveHandoffMode}
            onPickupHandoffModeChange={setSelectedHandoffMode}
            selfCollectOnlyGate={selfCollectOnlyGate}
            selfCollectConfirmed={selfCollectConfirmed}
            selfCollectConfirmAtKiosk={selfCollectConfirmAtKiosk}
            onSelfCollectConfirmedChange={setSelfCollectConfirmed}
            payOnSpotProximityNoticeMinutes={kioskInfo.data?.payOnSpotProximityNoticeMinutes}
            usePickupCopyKeys
            hideHeader
            salesPointName={
              kioskInfo.data?.name !== undefined
                ? resolveLocalizedName(
                    kioskInfo.data.name,
                    kioskInfo.data.nameLocales,
                    i18n.language,
                  )
                : null
            }
            salesPointLocation={
              kioskInfo.data
                ? resolveShopLocationLabelWithT(
                    {
                      customerPresence: kioskInfo.data.customerPresence,
                      location: kioskInfo.data.location,
                      locationMissingKey: 'retail.entry.shops.locationMissing',
                    },
                    t,
                  )
                : null
            }
          />
        ) : null}
        <div className="mx-auto w-full max-w-sm">
          <Button
            intent="primary"
            size="lg"
            block
            type="button"
            data-testid="customer-pickup-continue"
            disabled={continueBlocked}
            onClick={() => {
              if (shippingSelected) {
                const payload = shippingDraft.buildShippingPayload();
                if (payload != null) {
                  void shippingDraft.persistMembershipAddressIfRequested().finally(() => {
                    void handleShippingContinue(payload);
                  });
                }
                return;
              }
              void handleCollectContinue();
            }}
          >
            {continuePending ? t('checkout.loading') : t('pickup.continueToPayment')}
          </Button>
        </div>
        {paymentError !== null ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {toCustomerFacingErrorMessage(paymentError, {
              fallback: t('checkout.paymentStartFailed'),
            })}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
